import { z } from "zod";
import { BOARD_SIZE, MAX_SELFIE_BASE64_BYTES, TIMER_OPTIONS } from "./config";

export const seatResponseSchema = z.union([z.literal(1), z.literal(2)]);

export const tileResponseSchema = z
  .object({
    id: z.string().min(1).max(32),
    letter: z.string().regex(/^[A-Z?]$/u),
    value: z.number().int().min(0).max(10),
    assignedLetter: z
      .string()
      .regex(/^[A-Z]$/u)
      .optional()
  })
  .strict();

const boardTileResponseSchema = tileResponseSchema
  .extend({
    row: z
      .number()
      .int()
      .min(0)
      .max(BOARD_SIZE - 1),
    col: z
      .number()
      .int()
      .min(0)
      .max(BOARD_SIZE - 1),
    owner: seatResponseSchema,
    turn: z.number().int().positive()
  })
  .strict();

const publicPlayerResponseSchema = z
  .object({
    seat: seatResponseSchema,
    nickname: z.string().min(1).max(24),
    score: z.number().int(),
    tileCount: z.number().int().min(0).max(7)
  })
  .strict();

const currentPlayerResponseSchema = publicPlayerResponseSchema
  .extend({ rack: z.array(tileResponseSchema).max(7) })
  .strict();

const timerResponseSchema = z.union([
  z.null(),
  ...TIMER_OPTIONS.map((seconds) => z.literal(seconds))
]);

export const gameViewResponseSchema = z
  .object({
    code: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{10}$/u),
    status: z.enum(["waiting", "active", "complete"]),
    timerSeconds: timerResponseSchema,
    board: z.array(z.union([boardTileResponseSchema, z.null()])).length(BOARD_SIZE * BOARD_SIZE),
    bagCount: z.number().int().min(0).max(100),
    currentSeat: z.union([seatResponseSchema, z.null()]),
    revision: z.number().int().nonnegative(),
    consecutiveScorelessTurns: z.number().int().min(0).max(6),
    turnStartedAt: z.string().nullable(),
    turnDeadlineAt: z.string().nullable(),
    winningSeat: z.union([seatResponseSchema, z.null()]),
    completionReason: z.enum(["went_out", "scoreless", "resignation", "timeout"]).nullable(),
    parentRematchCode: z
      .string()
      .regex(/^[0-9A-HJKMNP-TV-Z]{10}$/u)
      .nullable(),
    rematchCode: z
      .string()
      .regex(/^[0-9A-HJKMNP-TV-Z]{10}$/u)
      .nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().nullable(),
    me: currentPlayerResponseSchema,
    opponent: publicPlayerResponseSchema.nullable(),
    selfieAvailable: z.boolean()
  })
  .strict();

export const moveViewResponseSchema = z
  .object({
    turnNumber: z.number().int().positive(),
    actingSeat: seatResponseSchema,
    actionType: z.enum(["place", "pass", "exchange", "resign", "timeout", "final_adjustment"]),
    payload: z.unknown(),
    words: z.array(
      z
        .object({
          word: z.string().regex(/^[A-Z]{2,15}$/u),
          score: z.number().int().nonnegative()
        })
        .strict()
    ),
    scoreDelta: z.number().int(),
    resultingScores: z.tuple([z.number().int(), z.number().int()]),
    createdAt: z.string()
  })
  .strict();

export const credentialResponseSchema = z
  .object({
    game: gameViewResponseSchema,
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    seat: seatResponseSchema
  })
  .strict();

export const actionResponseSchema = z
  .object({ game: gameViewResponseSchema, move: moveViewResponseSchema })
  .strict();

export const selfieRecordResponseSchema = z
  .object({
    mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
    base64: z.string().min(1).max(MAX_SELFIE_BASE64_BYTES),
    width: z.number().int().min(1).max(960),
    height: z.number().int().min(1).max(960),
    byteCount: z.number().int().min(1).max(MAX_SELFIE_BASE64_BYTES),
    sha256: z.string().regex(/^[0-9a-f]{64}$/u),
    updatedAt: z.string()
  })
  .strict();

export const responseSchemas = {
  health: z.object({ status: z.literal("ok") }).strict(),
  credentials: credentialResponseSchema,
  game: z.object({ game: gameViewResponseSchema }).strict(),
  moves: z.object({ moves: z.array(moveViewResponseSchema) }).strict(),
  action: actionResponseSchema,
  notificationConfig: z
    .object({
      publicKey: z.string().regex(/^[A-Za-z0-9_-]{87}$/u),
      enabled: z.boolean()
    })
    .strict(),
  notificationStatus: z.object({ enabled: z.boolean() }).strict(),
  selfie: z.object({ selfie: selfieRecordResponseSchema }).strict(),
  deleted: z.object({ deleted: z.boolean() }).strict()
} as const;

export const apiFailureResponseSchema = z
  .object({
    ok: z.literal(false),
    error: z
      .object({
        code: z.string().min(1),
        message: z.string().min(1),
        details: z.unknown().optional(),
        revision: z.number().int().nonnegative().optional()
      })
      .strict()
  })
  .strict();
